<template>
  <div class="company-sections-wrapper">
    <section id="company-details" class="d-flex flex-column gap-3">
      <h3>{{ $t('settings.company.details') }}</h3>
      <div class="row g-3">
        <LeadInput
          class="col-6"
          v-model="modelValue.name"
          :label="$t('settings.company.name')"
          :hint="$t('settings.company.name.hint')"
          :is-required="true"
          :inputClass="{ 'is-invalid': !modelValue.name } as any"
          :errors="errorsFn('name')" />
        <LeadInput
          class="col-6"
          v-model="modelValue.display_name"
          :label="$t('settings.company.display_name')"
          :hint="$t('settings.company.display_name.hint')"
          :errors="errorsFn('display_name')" />
      </div>
    </section>
    <CollapseContent
      v-model="showHierarchy"
      :name="$t('settings.company.hierarchy')"
      tag="section"
      class="mt-4"
      id="hierarchy-details">
      <div class="row g-3">
        <CompanySelector
          class="col-6"
          :required="false"
          :label="$t('settings.company.parent')"
          :hint="$t('settings.company.parent.hint')"
          v-model:companyId="modelValue.parent_company_id"
          :key="modelValue.id"
          :clearable="true"
          :errors="errorsFn('parent_company_id')" />
        <LeadSelect
          v-if="modelValue.parent_company_id"
          class="col-6"
          v-model="modelValue.level as any"
          :label="$t('settings.company.company_level')"
          :hint="$t('settings.company.company_level.hint')"
          :options="companyLevelOptions"
          :errors="errorsFn('level')" />
      </div>
    </CollapseContent>
    <section id="address-details" class="d-flex flex-column mt-4">
      <h3>{{ $t('settings.company.address') }}</h3>
      <div class="row g-3">
        <AddressSelector
          class="col-6"
          :label="$t('settings.company.address.street')"
          :hint="$t('settings.company.address.street.hint')"
          v-model="modelValue.address_street"
          @updated="updateAddressFields"
          :clearable="true"
          :required="false"
          :errors="errorsFn('address_street')" />
        <LeadInput
          class="col-6"
          v-model="modelValue.address_city"
          :label="$t('settings.company.address.city')"
          :hint="$t('settings.company.address.city.hint')"
          :errors="errorsFn('address_city')" />
      </div>
    </section>
    <section id="web-details" class="d-flex flex-column mt-4">
      <h3>{{ $t('settings.company.web_links') }}</h3>
      <div class="row">
        <LeadInput
          class="col-8"
          v-model="modelValue.homepage"
          :label="$t('settings.company.web_links.homepage')"
          :errors="errorsFn('homepage')"
          :hint="$t('settings.company.web_links.homepage.hint')"
          :inputClass="{ 'is-invalid': !props.isValidHomepage } as any" />
      </div>
    </section>
    <LoadingButton class="btn btn-primary mt-4" @press="(stopLoading) => emit('save', stopLoading)">
      {{ $t('company.save.button') }}
    </LoadingButton>
  </div>
</template>
<script setup lang="ts">
import type { IGoogleAddress } from '@/types/address';
import type { ICompany, CompanyLevel } from '@/types/company';
import CompanySelector from '@/components/company/CompanySelector.vue';
import AddressSelector from '@/components/common/AddressSelector.vue';
import { LoadingButton } from '@prospective/lithium';
import { ref, watch } from 'vue';
import CollapseContent from '../common/CollapseContent.vue';
import LeadInput from '../form/LeadInput.vue';
import LeadSelect from '../form/LeadSelect.vue';

const companyLevels: Record<CompanyLevel, string> = {
  subsidiary: 'Subsidiary',
  division: 'Division',
  business_unit: 'Business Unit',
  department: 'Department',
};

const companyLevelOptions = Object.entries(companyLevels).map(([value, label]) => ({
  value,
  label,
}));

const modelValue = defineModel<ICompany>({
  required: true,
  default: () => ({
    name: '',
    display_name: '',
    address_street: '',
    address_city: '',
    homepage: '',
    logo_image: { data: null },
    logo_uploaded_by: { type: 'user', data: null },
    default_dir: null,
  }),
});
const props = defineProps<{ isValidHomepage: boolean; errorsFn: (key: string) => string[] }>();
const emit = defineEmits<{
  (e: 'save', stopLoading: () => void): void;
}>();

const showHierarchy = ref(false);

/* --------------------------------- METHODS -------------------------------- */

function updateAddressFields(gAddress: IGoogleAddress | null) {
  if (!gAddress) {
    modelValue.value!.address_city = '';
    modelValue.value!.address_zip = '';
    modelValue.value!.address_country = '';
    return;
  }
  const addressComponents = gAddress.address_components || [];

  addressComponents.forEach((component) => {
    const types = component.types;

    if (types.includes('locality')) modelValue.value!.address_city = component.long_name;
    else if (types.includes('postal_code')) modelValue.value!.address_zip = component.long_name;
    else if (types.includes('country')) modelValue.value!.address_country = component.long_name;
  });
}

/* ---------------------------------- WATCH --------------------------------- */
watch(
  () => modelValue.value?.id,
  () => (showHierarchy.value = false)
);
watch(
  () => modelValue.value?.parent_company_id,
  (newVal?: number, oldVal?: number) => {
    if (!oldVal && newVal) modelValue.value!.level = 'subsidiary';
    else if (!newVal && oldVal) modelValue.value!.level = undefined;
  }
);
</script>
